import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  TableSortLabel,
  CircularProgress,
  Tooltip,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowRightTwoToneIcon from "@mui/icons-material/KeyboardArrowRightTwoTone";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SendSharpIcon from "@mui/icons-material/SendSharp";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { refinedUserstory, refineUserStoryWithRAG } from "../../../services/webServices";
import {
  MultipleUserStoriesContext,
  UserStoriesLoaderContext,
} from "../../main-pages/MultipleUserStoryPage";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const headers = [
  "Title",
  "Independent",
  "Negotiable",
  "Valuable",
  "Estimable",
  "Small",
  "Testable",
];

const MultipleEvaluationSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { userStoryLoaderState, setUserStoryLoaderState } = useContext(
    UserStoriesLoaderContext
  );
  const {
    multipleRefinedUserStory,
    setMultipleRefinedUserStory,
    setMultipleUseCases,
  } = useContext(MultipleUserStoriesContext);
  const [evaluationSummaryData, setEvaluationSummaryData] = useState([]);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const { setLoadingState } = useContext(LoadingModalContext);
  const [refineOption, setRefineOption] = useState(null);

  const  isRAGavaiable =
    location.state["isRAGavaiable"] !== undefined
      ? location.state["isRAGavaiable"]
      : false; 

  const rowsPerPage = 5;

  const [isCopied, setIsCopied] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText("Content to be copied");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleDownloadClick = () => setDownloadOpen(true);
  const handleCloseDownload = () => setDownloadOpen(false);

  const handleRowToggle = (index) => {
    setExpandedRow((prev) => (prev === index ? null : index));
  };

  const userStoryDetails =
    useContext(MultipleUserStoriesContext).multipleUserStories || [];
  const evaluationSummaryResponse = useContext(
    MultipleUserStoriesContext
  ).multipleEvaluationSummary;

  const defaultMultipleEvaluationSummaryExpanded =
    evaluationSummaryResponse.length > 0 &&
    multipleRefinedUserStory.length === 0;

  useEffect(() => {
    if (evaluationSummaryResponse.length > 0) {
      const combinedSummary = evaluationSummaryResponse.map(
        (evalSummary, index) => {
          const evalParametersArray = Array.isArray(evalSummary)
            ? evalSummary
            : [evalSummary];

          const title = userStoryDetails[index]?.Title || null;
          return {
            Title: title, //userStoryDetails[index]?.Title,
            EvaluationParameters: evalParametersArray
              .map((param) => ({
                Name: param?.Parameter,
                Rating: param?.Rating,
                Explanation: param?.Explanation,
                Recommendation: param?.Recommendation,
              }))
              .filter(
                (param) =>
                  param.Rating || param.Explanation || param.Recommendation
              ),
          };
        }
      ); //.filter(item => item.Title && item.EvaluationParameters.length > 0);

      const finalSummary =
        userStoryDetails.length === 0
          ? combinedSummary.fill({ Title: null, EvaluationParameters: [] }, 0)
          : combinedSummary.filter(
            (item) => item.Title && item.EvaluationParameters.length > 0
          );

      setEvaluationSummaryData(finalSummary);
    }
  }, [evaluationSummaryResponse]);

  // Formatting or flattening the test cases data values(Array or Objects) into strings
  const formattedEvaluationSummary = [];
  evaluationSummaryData.map((evalSummary) => {
    const flattenEvaluationSummary = {};

    Object.entries(evalSummary).map(([key, value]) => {
      if (Array.isArray(value)) {
        flattenEvaluationSummary[key] = flattenArray(value);
      } else if (!!value && typeof value === "object") {
        flattenEvaluationSummary[key] = flattenObject(value);
      } else {
        flattenEvaluationSummary[key] = value;
      }
    });

    formattedEvaluationSummary.push(flattenEvaluationSummary);
  });

  const handleRefinedUserStory = async () => {
    setUserStoryLoaderState("Refined User Stories");
    let progressHandler;
    progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 8000 });
    const initialMsg = evaluationSummaryResponse.length === 1 ? "Refining User Story" : "Refining User Stories";
    try {
      setMultipleRefinedUserStory([]);
      setMultipleUseCases([]);
      progressHandler.start(initialMsg);

      // Collect promises for concurrent execution
      const promises = evaluationSummaryResponse.map(async (eachRefiendUserStory, index) => {
        if (eachRefiendUserStory === null || !eachRefiendUserStory) {
          return null;
        }

        const requestData = {
          EvaluationSummary: eachRefiendUserStory,
          UserStory: userStoryDetails[index],
        };

        const refinedUserStories = await refinedUserstory(requestData);
        const flattenedRefinedUserStory = {};

        Object.entries(refinedUserStories.data.refinedUserStory).map(
          ([key, value]) => {
            if (Array.isArray(value)) {
              flattenedRefinedUserStory[key] = flattenArray(value);
            } else if (typeof value === "object" && !!value) {
              flattenedRefinedUserStory[key] = flattenObject(value);
            } else {
              flattenedRefinedUserStory[key] = value;
            }
          }
        );

        return { index, data: {...flattenedRefinedUserStory, id: userStoryDetails[index].id || null} };
      });

      // Await all promises in parallel
      const results = await Promise.all(promises);

      const updatedRefinedUserStories = [];

      results.forEach((result) => {
        if (result) {
          updatedRefinedUserStories[result.index] = result.data;

          setMultipleRefinedUserStory((prevState) => {
            const updatingRefinedUserStories = [...prevState];
            updatingRefinedUserStories[result.index] = result.data;
            return updatingRefinedUserStories;
          });
        } else {
          updatedRefinedUserStories.push(null);
        }
      });
      await progressHandler.finishAndClose();
      navigate("/dashboard/multi-userstory/output", {
        state: {
          ...location.state,
          multipleRefinedUserStory: [...updatedRefinedUserStories],
          selectedRefinedUserStories: Array(updatedRefinedUserStories.length).fill(null),
          multipleUseCases: [],
          selectedMultipleUseCases: [],

        },
        replace: true,
      });
    } catch (error) {
      console.log(error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setUserStoryLoaderState(null);
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleRefineWithRAG = async () => {
    setUserStoryLoaderState("Refined User Stories");
    let progressHandler;
    progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 8000 });
    const initialMsg = evaluationSummaryResponse.length === 1 ? "Refining User Story with RAG" : "Refining User Stories with RAG";
    try {
      setMultipleRefinedUserStory([]);
      setMultipleUseCases([]);
      progressHandler.start(initialMsg);

      // Collect promises for concurrent execution
      const promises = evaluationSummaryResponse.map(async (eachRefiendUserStory, index) => {
        if (eachRefiendUserStory === null || !eachRefiendUserStory) {
          return null;
        }
        const requestData = {
          EvaluationSummary: eachRefiendUserStory,
          UserStory: userStoryDetails[index],
        };
        const refinedUserStories = await refineUserStoryWithRAG(requestData);
        const flattenedRefinedUserStory = {};
        Object.entries(refinedUserStories.data.refinedUserStory).map(
          ([key, value]) => {
            if (Array.isArray(value)) {
              flattenedRefinedUserStory[key] = flattenArray(value);
            } else if (typeof value === "object" && !!value) {
              flattenedRefinedUserStory[key] = flattenObject(value);
            } else {
              flattenedRefinedUserStory[key] = value;
            }
          }
        );
        return { index, data: { ...flattenedRefinedUserStory, id: userStoryDetails[index].id || null } };
      });
      // Await all promises in parallel
      const results = await Promise.all(promises);
      const updatedRefinedUserStories = [];
      results.forEach((result) => {
        if (result) {
          updatedRefinedUserStories[result.index] = result.data;
          setMultipleRefinedUserStory((prevState) => {
            const updatingRefinedUserStories = [...prevState];
            updatingRefinedUserStories[result.index] = result.data;
            return updatingRefinedUserStories;
          });
        } else {
          updatedRefinedUserStories.push(null);
        }
      });
      await progressHandler.finishAndClose();
      navigate("/dashboard/multi-userstory/output", {
        state: {
          ...location.state,
          multipleRefinedUserStory: [...updatedRefinedUserStories],
          selectedRefinedUserStories: Array(updatedRefinedUserStories.length).fill(null),
          multipleUseCases: [],
          selectedMultipleUseCases: [],
          
        },
        replace: true,
      });
    } catch (error) {
      console.log(error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setUserStoryLoaderState(null);
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleRefine = () => {
    if (refineOption === "userStory") handleRefinedUserStory();
    else if (refineOption === "rag") handleRefineWithRAG();
  };

  const refineControls = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      <RadioGroup
        row
        value={refineOption}
        onChange={(e) => setRefineOption(e.target.value)}
        sx={{ display: "flex", gap: 2 }}
      >
        <FormControlLabel
          value="userStory"
          control={<Radio sx={{ "& .MuiSvgIcon-root": { fontSize: 14 } }} />}
          label="Refine User Story"
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
        />
        <FormControlLabel
          value="rag"
          control={<Radio disabled={!isRAGavaiable} sx={{ "& .MuiSvgIcon-root": { fontSize: 14 } }} />}
          label="Refine With RAG"
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
          disabled={!isRAGavaiable}
        />
      </RadioGroup>
      <Button
        variant="contained"
        onClick={handleRefine}
        disabled={!refineOption || !!userStoryLoaderState || (refineOption === 'rag' && !isRAGavaiable)}
        sx={{ textTransform: "none", fontSize: 14, backgroundColor: "#49A3FF", whiteSpace: "nowrap" }}
      >
        Refine
      </Button>
    </Box>
  );

  const handlePageChange = (event, value) => setCurrentPage(value);

  const handleSort = (headerIndex) => {
    const key = headers[headerIndex];
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    const direction = isAsc ? "desc" : "asc";

    const sortedData = [...evaluationSummaryData].sort((a, b) => {
      const ratingA = parseInt(a.EvaluationParameters[headerIndex - 1].Rating);
      const ratingB = parseInt(b.EvaluationParameters[headerIndex - 1].Rating);
      if (ratingA < ratingB) return direction === "asc" ? -1 : 1;
      if (ratingA > ratingB) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortConfig({ key, direction });
    setEvaluationSummaryData(sortedData);
  };

  const paginatedData = evaluationSummaryData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // const paginatedData = React.useMemo(() => {
  //   return evaluationSummaryData.slice(
  //     (currentPage - 1) * rowsPerPage,
  //     currentPage * rowsPerPage
  //   );
  // }, [evaluationSummaryData, currentPage]);

  const getRatingStyle = (rating) => {
    if (rating <= 2) {
      return { backgroundColor: " #FFDAD7" }; // Red
    } else if (rating === 3) {
      return { backgroundColor: "#fff176" }; // Yellow
    } else if (rating >= 4) {
      return { backgroundColor: "#D0FFAA" }; // Green
    }
    return {};
  };

  const tableRender = () => {
    return (
      <Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            overflow: "none",
            border: "none",
            borderRadius: 0,
            // paddingRight: 5,
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f0f0f0",
            },
            scrollbarWidth: "thin", // Firefox
            scrollbarColor: "#c1c1c1 #f0f0f0",
          }}
        >
          <Table
            stickyHeader
            sx={{
              borderCollapse: "collapse",
              border: "1px solid #ccc", // full table border
            }}
          >
            <TableHead>
              <TableRow>
                {headers.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      border: "1px solid #ccc",
                      position: "sticky",
                      top: 0,
                      left: index === 0 ? 0 : "auto",
                      zIndex: index === 0 ? 3 : 1,
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      textAlign: index === 0 ? "left" : "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 400,
                        letterSpacing: 0.4,
                        lineHeight: "16px",
                      }}
                    >
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.map((evaluationGroup, groupIndex) => {
                const { Title, EvaluationParameters } = evaluationGroup;
                const isExpanded = expandedRow === groupIndex;
                const bgColor = groupIndex % 2 === 0 ? "#e3f2fd" : "#ffffff";

                return (
                  <React.Fragment key={groupIndex}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor: bgColor,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: bgColor,
                        },
                      }}
                      onClick={() => handleRowToggle(groupIndex)}
                    >
                      <TableCell
                        sx={{
                          border: "1px solid #ccc",
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          backgroundColor: bgColor,
                          "&:hover": {
                            backgroundColor: bgColor,
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowToggle(groupIndex);
                            }}
                          >
                            {isExpanded ? <ExpandMoreIcon /> : <KeyboardArrowRightTwoToneIcon />}
                          </IconButton>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              minWidth: "250px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              overflow: "visible",
                              textOverflow: "unset",
                            }}
                          >
                            {Title}
                          </Typography>
                        </Box>
                      </TableCell>
                      {EvaluationParameters?.map(({ Rating }, paramIndex) => (
                        <TableCell
                          key={`rating-${groupIndex}-${paramIndex}`}
                          sx={{
                            border: "1px solid #ccc",
                            color:
                              Rating === 1 || Rating === 2
                                ? "red"
                                : Rating === 3
                                  ? "orange"
                                  : "green",
                            fontWeight: "bold",
                            textAlign: "center",
                            minWidth: "200px",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            backgroundColor: bgColor,
                            "&:hover": {
                              backgroundColor: bgColor,
                            },
                          }}
                        >
                          {Rating}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Expanded Rows */}
                    {isExpanded &&
                      ["Explanation", "Recommendation"].map((label, rowIndex) => {
                        const expandedBg =
                          (groupIndex + rowIndex + 1) % 2 === 0 ? "#f8f8f8" : "#ffffff";

                        return (
                          <TableRow
                            key={`${label}-${groupIndex}`}
                            sx={{
                              backgroundColor: expandedBg,
                              "&:hover": {
                                backgroundColor: expandedBg,
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                border: "1px solid #ccc",
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                                backgroundColor: expandedBg,
                                textAlign: "center",
                                "&:hover": {
                                  backgroundColor: expandedBg,
                                },
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {label}
                              </Typography>
                            </TableCell>
                            {EvaluationParameters?.map((param, paramIndex) => (
                              <TableCell
                                key={`${label}-${groupIndex}-${paramIndex}`}
                                sx={{
                                  border: "1px solid #ccc",
                                  backgroundColor: expandedBg,
                                  textAlign: "center",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  "&:hover": {
                                    backgroundColor: expandedBg,
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: 14, fontWeight: 400 }}
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      param[label]?.toString().replace(/\n/g, "<br>") || "-",
                                  }}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          count={Math.ceil(evaluationSummaryData.length / rowsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          sx={{ mt: 2, display: "flex", justifyContent: "center" }}
        />

        <Box sx={{ display: "flex", alignItems: "center", margin: 2 }}>
          <Tooltip title="Like" arrow>
            <Box
              onClick={() => handleThumbClick("like")}
              sx={{
                cursor: "pointer",
                marginRight: 2,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {activeThumb === "like" ? (
                <ThumbUpIcon sx={{ fontSize: "24px" }} />
              ) : (
                <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
              )}
            </Box>
          </Tooltip>

          <Tooltip title="Dislike" arrow>
            <Box
              onClick={() => handleThumbClick("dislike")}
              sx={{
                cursor: "pointer",
                marginRight: 2,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {activeThumb === "dislike" ? (
                <ThumbDownIcon sx={{ fontSize: "24px" }} />
              ) : (
                <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
              )}
            </Box>
          </Tooltip>

          <Tooltip title={isCopied ? "Copied!" : "Copy"} arrow>
            <Box
              onClick={handleCopyClick}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {isCopied ? (
                <ContentCopyIcon sx={{ fontSize: "24px" }} />
              ) : (
                <ContentCopyOutlinedIcon sx={{ fontSize: "24px" }} />
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  };



  const createWordData = (evaluationSummaryTableData) => {
    const wordData = [];
    evaluationSummaryTableData.map((eachSummary, index) => {
      if (!!eachSummary) {
        const headers = Object.keys(eachSummary[0]);
        const values = eachSummary.map((eachParameterSummary) =>
          Object.values(eachParameterSummary)
        );
        wordData.push({
          heading: "User Story",
          type: "text",
          data: userStoryDetails[index]["Title"],
        });
        wordData.push({
          heading: "Evaluation Summary",
          type: "table",
          data: { headers, values },
        });
        wordData.push({ type: "pageBreak" });
        return;
      }
    });
    return wordData;
  };

  const createPdfData = (summariesData) => {
    const validSummaries = summariesData.filter((summary) => summary != null);

    const tableData = (data, index) => {
      if (
        !Array.isArray(data) ||
        data.length === 0 ||
        typeof data[0] !== "object" ||
        data[0] === null
      ) {
        console.warn(
          `Invalid or empty data for summary at index ${index}:`,
          data
        );
        return { headers: ["No data available"], values: [["-"]] };
      }

      const headers = Object.keys(data[0]);
      const values = data.map((row) => headers.map((key) => row[key] ?? "-"));

      return { headers, values };
    };

    return validSummaries.map((eachSummary, index) => ({
      heading: `Evaluation Summary Report - ${index + 1}`,
      type: "table",
      data: tableData(eachSummary, index),
    }));
  };

  return (
    <Box>
      <CommonAccordion
        summary={"Evaluation Summary"}
        details={tableRender()}
        type={"output"}
        buttons={[
          {
            label: "Download",
            onClick: handleDownloadClick,
            variant: "text",
            startIcon: <FileDownloadOutlinedIcon />,
          },
          {
            label: refineControls,
            variant: "text",
            sx: {
              textTransform: "none",
              cursor: "default",
              "&.Mui-disabled": {
                color: "black",
                opacity: 1,
              },
              float: "right"
            },
          },
        ]}
        fullScreenMode={true}
        defaultExpanded={defaultMultipleEvaluationSummaryExpanded}
      />
      <CommonDownload
        open={downloadOpen}
        onClose={handleCloseDownload}
        wordData={createWordData(
          evaluationSummaryResponse.filter((item) => item != null)
        )}
        serviceName="Evaluation Summary Report"
        serviceResponse={createWordData(
          evaluationSummaryResponse.filter((item) => item != null)
        )}
        documents={["docx", "pdf"]}
      />
    </Box>
  );
};

export default MultipleEvaluationSummary;
